import { Component, EventEmitter, OnDestroy, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import { DataStorageService } from "../../../services/data-storage.service";
import {
  Claim,
  ClaimStatus,
  FnolRequest, GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  PetInsuranceData,
  YearListItem
} from "../../../models";
import { Subject, takeUntil } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { DialogService } from "../../../services/dialog.service";

@Component({
  selector: 'cpf-claims-list',
  templateUrl: './claims-list.component.html',
  styleUrls: ['./claims-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClaimsListComponent implements OnDestroy {
  public petData?: PetInsuranceData;
  public claims: Claim[] = [];
  public claimsYearList: YearListItem<Claim>[] = [];
  public newFnolRequestList: YearListItem<FnolRequest>[] = [];
  private destroy$ = new Subject<void>();
  @Output() claimClicked = new EventEmitter<Claim>();
  @Output() fnolDetailsClicked = new EventEmitter<FnolRequest>();

  protected readonly ClaimStatus = ClaimStatus;

  constructor(private router: Router,
              private dataStorageService: DataStorageService,
              private translate: TranslateService,
              private dialog: MatDialog,
              private dialogService: DialogService,) {
    this.setSelectedPetData(this.dataStorageService.selectedData);

    this.dataStorageService.selectedData$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((pet) => {
      this.setSelectedPetData(pet);
    });

    this.dataStorageService.fnolRequests$.pipe(takeUntil(this.destroy$)).subscribe((fnolRequests: FnolRequest[]) => {
      this.setNewFnolRequests(fnolRequests);
    })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSelectedPetData(pet: PetInsuranceData | undefined) {
    const selectedData = pet;

    if (selectedData) {
      this.petData = selectedData;
      this.claims = selectedData.claims || [];
      if (this.claims.length > 0) {
        this.claimsYearList = this.groupByYear(this.claims);
      } else {
        this.claimsYearList = [];
      }
    }

    if (this.claimsYearList.length >= 1) {
      this.claimsYearList[0].expanded = true;
    }
  }

  setNewFnolRequests(fnolRequests: FnolRequest[]) {
    this.newFnolRequestList = [];

    if (fnolRequests.length) {
      this.translate.get('claims-list.new-claims-from-cms').subscribe((label) => {
        this.newFnolRequestList = [{
          year: label,
          expanded: true,
          items: fnolRequests.map(item => ({
            ...item,
            expected_date: item.labels?.includes('simple_claim') ? new Date(new Date(item.notificationDate).getTime() + 21 * 24 * 60 * 60 * 1000) : new Date(new Date(item.notificationDate).getTime() + 70 * 24 * 60 * 60 * 1000)
          }))
        }]
      })
    }
  }

  toggleYear(year: YearListItem<Claim | FnolRequest>) {
    year.expanded = !year.expanded;
  }

  openClaim(claim: Claim) {
    if (this.claimClicked.observed) {
      this.claimClicked.emit(claim);
    } else {
      this.router.navigate(['calingo', 'pet', 'claims', claim.identifier])
    }
  }

  openFnolRequest(fnolRequest: FnolRequest) {
    if (this.fnolDetailsClicked.observed) {
      this.fnolDetailsClicked.emit(fnolRequest);
    } else {
      this.router.navigate(['calingo', 'pet', 'fnol-details', fnolRequest.id])
    }
  }

  formatStatus(status: ClaimStatus): "claims-list.status.NEW" | "claims-list.status.IN_PROGRESS" | "claims-list.status.DONE" | "claims-list.status.COMPLETED" | "claims-list.status.OTHER" {
    if (status == ClaimStatus.NEW) {
      return "claims-list.status.NEW"
    }
    if (status == ClaimStatus.IN_PROGRESS) {
      return "claims-list.status.IN_PROGRESS"
    }
    if (status == ClaimStatus.DONE) {
      return "claims-list.status.DONE"
    }
    if (status == ClaimStatus.COMPLETED) {
      return "claims-list.status.COMPLETED";
    }
    return "claims-list.status.OTHER"
  }

  getStatusClass(status: ClaimStatus): "status-new" | "status-in-progress" | "status-done" | "status-completed" | "status-other" {
    if (status == ClaimStatus.NEW) {
      return "status-new";
    }
    if (status == ClaimStatus.IN_PROGRESS) {
      return "status-in-progress";
    }
    if (status == ClaimStatus.DONE) {
      return "status-done";
    }
    if (status == ClaimStatus.COMPLETED) {
      return "status-completed";
    }
    return "status-other";
  }

  private groupByYear(claims: Claim[]): YearListItem<Claim>[] {
    const result: YearListItem<Claim>[] = [];
    claims.forEach(claim => {
      const year = new Date(claim.claim_date).getFullYear();
      const yearListItem = result.find(item => item.year === year.toString());

      if (yearListItem) {
        yearListItem.items.push(claim);
      } else {
        result.push({ year: year.toString(), expanded: false, items: [claim] });
      }
    });

    // Sort the years in descending order
    result.sort((a, b) => isNaN(+a.year) === isNaN(+b.year) ? (isNaN(+a.year) ? a.year.localeCompare(b.year) : +b.year - +a.year) : isNaN(+a.year) ? -1 : 1);

    result.forEach(item => {
      item.items.sort((a, b) => b.identifier.localeCompare(a.identifier, undefined, { numeric: true }));
    })

    return result;
  }

  openInfo(event: MouseEvent) {
    event.stopPropagation();
    this.translate.get(['claims-list.expected-info-popup.title', 'claims-list.expected-info-popup.message']).subscribe(translations => {
      const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
        data: {
          title: translations['claims-list.expected-info-popup.title'],
          message: translations['claims-list.expected-info-popup.message'],
          confirmButton: GeneralConfirmationModalResult.Confirm
        } as GeneralConfirmationModalData,
      });

      dialogRef.afterClosed().subscribe(() => {

      });
    })
  }
}
